// TopBanners — the ONE fixed strip at the top of the authenticated shell that
// holds every app-wide status banner (support view, platform mode, reads
// paused). It is `position: fixed`, so it never takes height out of the
// html → body → #root `h-full` chain the pages are laid out in.
//
// WHY (the S2-1 finding on #1963, measured 2026-09-04 at 390×844): the three
// banners used to mount as in-flow `sticky top-0` siblings inside #root. In
// flow, a banner steals its own height from #root, and RailShell's `h-full`
// then overhangs the bottom by exactly that much — the bottom ~30px of every
// bounded scroller sat under the fixed bottom nav whenever "Connection lost"
// was up, an ordinary orchard state. main's 88px of triple-reserved nav space
// hid it; the moment #1963 removes that cushion it lands on the bar.
//
// HOW: this strip measures its own height into `--top-banner-h` on <html>.
// index.css folds that into `--top-inset` (the larger of the notch inset and
// the banner strip), which is what <body>'s padding-top, `.sticky-top-inset`
// and PageHeader all read — so #root shrinks by the banner instead of the
// banner eating into it, the same trick `has-bottom-nav` plays at the foot.
// Measured, not assumed: three banners can stack and their heights depend on
// text wrapping at the viewport width.
import { useLayoutEffect, useRef, type ReactNode } from 'react'

export const TOP_BANNER_VAR = '--top-banner-h'

export function writeTopBannerHeight(el: HTMLElement | null, root: HTMLElement = document.documentElement) {
  const h = el ? Math.round(el.getBoundingClientRect().height) : 0
  root.style.setProperty(TOP_BANNER_VAR, `${h}px`)
  return h
}

export default function TopBanners({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    const write = () => writeTopBannerHeight(el, root)
    write()
    // ResizeObserver catches a banner mounting / unmounting / rewrapping; the
    // guard is for jsdom and very old WebViews, which get the one-shot write.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(write) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      root.style.removeProperty(TOP_BANNER_VAR)
    }
  }, [])
  return (
    <div ref={ref} className="top-banners print:hidden" data-testid="top-banners">
      {children}
    </div>
  )
}
